import { useCallback, useEffect, useState } from 'react'
import type { WeeklyKpiPayload, WeeklyKpiRecord } from '@ai-meeting/shared'
import { useUiStrings } from '@/hooks/useUiStrings'
import {
  draftWeeklyKpi,
  downloadWeeklySlides,
  fetchWeeklyKpi,
  saveWeeklyKpi,
} from '@/services/weeklyReport'
import {
  formatWeekRange,
  kpiWeekOf,
  reportWeekOf,
  shiftWeeks,
  type ReportWeek,
} from '@/utils/reportWeek'

/**
 * The project's Weekly Report tab.
 *
 * Opening it never calls the AI: a week costs a model pass of tens of seconds
 * and real money, so it happens only when somebody asks.
 *
 * The week leaves as a deck. There is no second button that writes the report
 * out on screen first — the deck is what the reader wants and it is built from
 * the week directly, not from a stored report, so asking for one before the
 * other would have spent a second pass to show a draft of the first. The
 * endpoints that generate and keep a report are still there and still work;
 * nothing in this tab calls them.
 */
export default function ProjectWeeklyReportTab({ projectKey }: { projectKey: string }) {
  const { t } = useUiStrings()
  /** The day both weeks are derived from; the arrows move it a week at a time. */
  const [anchor, setAnchor] = useState(() => new Date())
  const reportWeek = reportWeekOf(anchor)
  const kpiWeek = kpiWeekOf(anchor)

  const [kpi, setKpi] = useState<WeeklyKpiRecord | null>(null)
  const [busy, setBusy] = useState<'kpi' | 'slides' | 'saving' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadWeek = useCallback(async () => {
    setError(null)
    try {
      const savedKpi = await fetchWeeklyKpi(projectKey, kpiWeek.weekStart)
      setKpi(savedKpi.kpi)
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedLoadWeeklyReport)
    }
  }, [projectKey, kpiWeek.weekStart, t.failedLoadWeeklyReport])

  useEffect(() => {
    void loadWeek()
  }, [loadWeek])

  /** Runs one of the slow calls, keeping its button down and its failure on screen. */
  async function run(kind: 'kpi' | 'slides' | 'saving', work: () => Promise<void>) {
    if (busy) return
    setBusy(kind)
    setError(null)
    try {
      await work()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedGenerateWeeklyReport)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-6" data-testid="weekly-report-tab">
      <WeekPicker
        label={t.weeklyReportWeekLabel}
        week={reportWeek}
        disabled={Boolean(busy)}
        onShift={(offset) => setAnchor((current) => shiftWeeks(current, offset))}
        previousLabel={t.weeklyReportPreviousWeek}
        nextLabel={t.weeklyReportNextWeek}
      />

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-primary rounded-md px-3 py-1.5 text-sm"
          disabled={Boolean(busy)}
          onClick={() =>
            void run('slides', async () => {
              const deck = await downloadWeeklySlides(projectKey, reportWeek)
              saveBlob(deck.blob, deck.filename)
            })
          }
        >
          {busy === 'slides' ? t.weeklyReportExporting : t.weeklyReportExportSlides}
        </button>
      </div>

      <KpiSection
        projectKey={projectKey}
        week={kpiWeek}
        kpi={kpi}
        busy={busy}
        onDraft={() =>
          void run('kpi', async () => {
            setKpi(await draftWeeklyKpi(projectKey, kpiWeek))
          })
        }
        onSave={(payload) =>
          void run('saving', async () => {
            const result = await saveWeeklyKpi(projectKey, { ...kpiWeek, payload })
            setKpi(result.kpi)
          })
        }
        onShift={(offset) => setAnchor((current) => shiftWeeks(current, offset))}
      />
    </div>
  )
}

function WeekPicker({
  label,
  week,
  disabled,
  onShift,
  previousLabel,
  nextLabel,
}: {
  label: string
  week: ReportWeek
  disabled: boolean
  onShift: (offset: number) => void
  previousLabel: string
  nextLabel: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
        {label}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={previousLabel}
          disabled={disabled}
          onClick={() => onShift(-1)}
          className="rounded-md border border-[var(--border-color)] px-2 py-1 text-xs disabled:opacity-50"
        >
          ←
        </button>
        <span className="min-w-[10rem] text-center text-sm font-semibold tabular-nums">
          {formatWeekRange(week)}
        </span>
        <button
          type="button"
          aria-label={nextLabel}
          disabled={disabled}
          onClick={() => onShift(1)}
          className="rounded-md border border-[var(--border-color)] px-2 py-1 text-xs disabled:opacity-50"
        >
          →
        </button>
      </div>
    </div>
  )
}

function EmptyNote({ text }: { text: string }) {
  return <p className="text-sm text-[var(--text-secondary)]">{text}</p>
}

function KpiSection({
  week,
  kpi,
  busy,
  onDraft,
  onSave,
  onShift,
}: {
  projectKey: string
  week: ReportWeek
  kpi: WeeklyKpiRecord | null
  busy: string | null
  onDraft: () => void
  onSave: (payload: WeeklyKpiPayload) => void
  onShift: (offset: number) => void
}) {
  const { t } = useUiStrings()
  const [draft, setDraft] = useState<WeeklyKpiPayload | null>(null)

  // The form follows whatever the tab last loaded or drafted; a local edit
  // that outlived a week change would be filed against the wrong week.
  useEffect(() => {
    setDraft(kpi?.payload ?? null)
  }, [kpi])

  return (
    <div className="space-y-3 border-t border-[var(--border-color)] pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold text-[var(--text-primary)]">{t.weeklyKpiHeading}</h2>
        <WeekPicker
          label={t.weeklyKpiWeekLabel}
          week={week}
          disabled={Boolean(busy)}
          onShift={onShift}
          previousLabel={t.weeklyReportPreviousWeek}
          nextLabel={t.weeklyReportNextWeek}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-secondary rounded-md px-3 py-1.5 text-sm"
          disabled={Boolean(busy)}
          onClick={onDraft}
        >
          {busy === 'kpi' ? t.weeklyKpiDrafting : t.weeklyKpiDraft}
        </button>
        {draft && (
          <button
            type="button"
            className="btn-primary rounded-md px-3 py-1.5 text-sm"
            disabled={Boolean(busy)}
            onClick={() => onSave(draft)}
          >
            {busy === 'saving' ? t.weeklyKpiSaving : t.weeklyKpiSave}
          </button>
        )}
        {kpi?.edited && (
          <span className="text-xs text-[var(--text-secondary)]">{t.weeklyKpiEdited}</span>
        )}
      </div>

      {!draft ? (
        <EmptyNote text={t.weeklyKpiEmpty} />
      ) : (
        <div className="space-y-3">
          <p className="text-sm">
            <span className="font-semibold">{t.weeklyKpiHealth}:</span>{' '}
            {draft.health ?? t.weeklyKpiNotAssessed}
            {' · '}
            <span className="font-semibold">{t.weeklyKpiFollowUp}:</span>{' '}
            {draft.follow_up_status ?? t.weeklyKpiNotAssessed}
          </p>
          <KpiField
            label={t.weeklyKpiOverallStatus}
            value={draft.overall_status}
            onChange={(value) => setDraft({ ...draft, overall_status: value })}
          />
          <KpiField
            label={t.weeklyKpiAchievement}
            value={draft.achievement}
            onChange={(value) => setDraft({ ...draft, achievement: value })}
          />
          <KpiField
            label={t.weeklyKpiIssues}
            value={draft.issues}
            none={draft.no_issues}
            onChange={(value) => setDraft({ ...draft, issues: value })}
          />
          <KpiField
            label={t.weeklyKpiRisk}
            value={draft.risk}
            none={draft.no_risk}
            onChange={(value) => setDraft({ ...draft, risk: value })}
          />
          <KpiField
            label={t.weeklyKpiSupport}
            value={draft.support}
            none={draft.no_support_needed}
            onChange={(value) => setDraft({ ...draft, support: value })}
          />
        </div>
      )}
    </div>
  )
}

/**
 * One box of the KPI form.
 *
 * A box the rules marked "none" is shown as such rather than as an empty one
 * somebody forgot to fill: the AI service blanks it on purpose, and an empty
 * textarea invites the reader to write into a field that will be cleared.
 */
function KpiField({
  label,
  value,
  none,
  onChange,
}: {
  label: string
  value: string
  none?: boolean
  onChange: (value: string) => void
}) {
  const { t } = useUiStrings()
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--text-secondary)]">{label}</span>
      {none ? (
        <p className="mt-1 text-sm italic text-[var(--text-secondary)]">{t.weeklyKpiNone}</p>
      ) : (
        <textarea
          className="mt-1 w-full rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] p-2 text-sm"
          rows={3}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </label>
  )
}

/** Hands the browser a file to save, then lets go of the object URL. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
